import React from "react";
import { formatFormDate, convertToTimeZone } from "../../../helper/common";
import { renderHtmlContent } from "./exportRenderForms";
import { label, quote } from "../../../config";
import { number } from "../../../config/constants";
import { nbspRegex, removeHtmlTags, spaceRegex } from "../../../config/regex";

/**
 * Check if the description has meaningful text
 * @param {string} html - The HTML content of the description
 * @returns {boolean} - True if the description has meaningful text, false otherwise
 */
const hasMeaningfulDescriptionText = (html) => {
  if (!html) return false;
  const text = html
    .replace(removeHtmlTags, " ")
    .replace(nbspRegex, " ")
    .replace(spaceRegex, " ")
    .trim();
  return text.length > number.ZERO;
};

/**
 * Render description section
 */
export const renderDescription = (taskData) => {
  const html = taskData?.description ?? "";
  const hasDescription = hasMeaningfulDescriptionText(html);

  return (
    <div className="description-content">
      {hasDescription ? (
        <div className="read-only-editor">{renderHtmlContent(html)}</div>
      ) : (
        <p>{quote.NO_DESCRIPTION_ADDED}</p>
      )}
    </div>
  );
};

/**
 * Render approvals section
 */
export const renderApprovals = (approvalLogs) => {
  if (!approvalLogs || !Array.isArray(approvalLogs) || approvalLogs?.length === number.ZERO) {
    return (
      <div className="approvals-content">
        <p>{quote.NO_APPROVAL_INITIATED}</p>
      </div>
    );
  }

  const sortedApprovalLogs = [...approvalLogs].sort((a, b) => {
    const timestampA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
    const timestampB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
    return timestampB - timestampA;
  });

/**
 * Format date and time in user timezone
 * @author Bhavana
 */
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const formattedDate = convertToTimeZone(dateString, 'dd LLL yy, h:mmaaa');
    return formattedDate || 'N/A';
  };

  return (
    <div className="approvals-content">
      <table className="approvals-table w-100 m-0">
        <thead>
          <tr>
            <th className="approval-table-header p-2 text-left">Date & Time</th>
            <th className="approval-table-header p-2 text-left">Logs</th>
          </tr>
        </thead>
        <tbody>
          {sortedApprovalLogs.map((log, index) => {
            const formattedDateTime = formatDateTime(log.CreatedAt);
            const rawActivity = log.Activity || 'No activity recorded';
            const activity = rawActivity.replace(/<[^>]*>/g, '').trim();

            return (
              <tr key={index}>
                <td className="approval-table-cell p-2 text-left align-top">{formattedDateTime}</td>
                <td className="approval-table-cell p-2 text-left align-top">
                  {activity}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Render emails section
 */
export const renderEmails = () => (
  <div className="emails-content">
    <p>No emails available for this task</p>
  </div>
);

/**
 * Render attachments section
 */
export const renderAttachments = () => (
  <div className="attachments-content">
    <p>No attachments available for this task</p>
  </div>
);

/**
 * Render activity logs section
 */
export const renderActivityLogs = () => (
  <div className="activity-logs-content">
    <p>No activity logs available for this task</p>
  </div>
);

/**
 * Render comments section
 */
export const renderComments = () => (
  <div className="comments-content">
    <p>No comments available for this task</p>
  </div>
);

/**
 * Render linked tasks section
 */
export const renderLinkedTasks = () => (
  <div className="linked-tasks-content">
    <p>No linked tasks available for this task</p>
  </div>
);

/**
 * Render tags section
 */
export const renderTags = () => (
  <div className="tags-content">
    <p>No tags available for this task</p>
  </div>
);

/**
 * Render followers section
 */
export const renderFollowers = () => (
  <div className="followers-content">
    <p>No followers available for this task</p>
  </div>
);

/**
 * Get assignee label
 */
const getAssigneeLabel = (assigneeList, assigneeValue) =>
  assigneeList?.find((a) => String(a?.value) === String(assigneeValue))?.label ?? "";

/**
 * Submission / last-updated lines for PDF — mirrors TaskFormHeader.jsx metadata behavior.
 */
const buildFormSectionSubmissionInfo = (formData) => {
  const submitterName = formData?.submitterDetails?.name ?? formData?.submitterName;
  const createdAt = formData?.createdAt;
  if (!submitterName || !createdAt) {
    return null;
  }
  const submittedByText = `${label.SUBMITTED_BY} ${submitterName} ${label.ON} ${formatFormDate(createdAt)}`;
  const lastUpdatedByText = formData?.lastUpdatedByName && formData?.updatedAt
      ? `${label.LAST_UPDATED_BY} ${formData.lastUpdatedByName} ${label.ON} ${formatFormDate(formData.updatedAt)}`
      : null;
  return lastUpdatedByText ? `${submittedByText}, ${lastUpdatedByText}` : submittedByText;
};

/**
 * Build submission info for review section
 */
const buildReviewSectionSubmissionInfo = (formData, assigneeList) => {
  const lastUpdatedByDisplayName = getAssigneeLabel(assigneeList, formData?.reviewLastUpdatedBy);
  const lastUpdatedAt = formData?.reviewLastUpdatedAt;
  if (!lastUpdatedByDisplayName || !lastUpdatedAt) {
    return null;
  }
  return `${label.LAST_UPDATED_BY} ${lastUpdatedByDisplayName} ${label.ON} ${formatFormDate(lastUpdatedAt)}`;
};

/**
 * Render section with conditional logic
 * @param {string} assigneeList - from defaultDetails (same as TaskFormHeader)
 */
export const renderSection = (sectionKey, sectionTitle, content, formData, assigneeList) => {
  const submissionHeaderKeys = ["forms", "reviewSection"];

  if (!submissionHeaderKeys.includes(sectionKey)) {
    return (
      <div className="pdf-section mb-4" key={sectionKey}>
        <h3 className="section-title mb-2 pb-1">{sectionTitle}</h3>
        <div className="section-content ml-2">{content}</div>
      </div>
    );
  }
  const submissionInfo = sectionKey === "forms" ? buildFormSectionSubmissionInfo(formData) : buildReviewSectionSubmissionInfo(formData, assigneeList);

  if (!submissionInfo) {
    return (
      <div className="pdf-section mb-4" key={sectionKey}>
        <h3 className="section-title mb-2 pb-1">{sectionTitle}</h3>
        <div className="section-content ml-2">{content}</div>
      </div>
    );
  }

  return (
    <div className="pdf-section mb-4" key={sectionKey}>
      <div className="section-header-with-info d-flex flex-column mb-3">
        <h3 className="section-title mb-1 pb-1">{sectionTitle}</h3>
        <div className="submission-info">
          <span>{submissionInfo}</span>
        </div>
      </div>
      <div className="section-content ml-2">{content}</div>
    </div>
  );
};
